import React, { useMemo } from "react";
import { gql } from "@apollo/client";
import { useHrisSyncSidebarQuery } from "./HrisSyncDetailSidebar.generated";
import SidebarHeader, {
  SidebarContent,
  SidebarTitle,
} from "@src/features/sidebars/ui/SidebarHeader";
import InfoTable, { InfoTableItem } from "@src/lib/ui/InfoTable";
import SidebarLink from "@src/features/sidebars/ui/SidebarLink";
import { format, parseISO } from "date-fns";
import Text from "@src/lib/ui/Text";
import { DetailSidebarType } from "@src/features/sidebars/detailSidebar";
import ArrowsRefreshIcon from "@src/lib/icons/18px/arrows-refresh";
import UserOutlineIcon from "@src/lib/icons/18px/user-outline";
import PaginatedTable from "@src/lib/ui/PaginatedTable";
import {
  HrisEmployeeFragment,
  useHrisEmployeesLazyQuery,
  useHrisEmployeesQuery,
} from "@src/features/settings/hris/HrisEmployeeListContainer.generated";
import { HrisEmployeesInput } from "@src/types.generated";
import CommaSeparatedLinks from "@src/features/sidebars/ui/CommaSeparatedLinks";
import {
  QueryTabs,
  QueryTabRenderFns,
  QueryTabCopy,
  QueryTabCounts,
} from "@src/lib/ui/QueryTabs";

type Props = {
  rowId: number;
  showHeader?: boolean;
};

const HrisSyncSidebar: React.FC<Props> = ({ rowId, showHeader = true }) => {
  const { data } = useHrisSyncSidebarQuery({
    variables: {
      id: rowId,
    },
    skip: !rowId,
  });
  const inputFilters: HrisEmployeesInput = useMemo(() => {
    return {
      filters: {
        mergeBackfillRunId: { value: rowId },
      },
    };
  }, [rowId]);

  // Fetch total count for the tab badge
  const { data: employeesCountData } = useHrisEmployeesQuery({
    variables: {
      input: inputFilters,
      pagination: { limit: 0 }, // Only need the count, not the data
    },
    skip: !rowId,
  });

  const columns = ["name", "hrisId", "status", "location", "role"] as const;
  type Column = (typeof columns)[number];

  const tabCounts: QueryTabCounts<"details" | "employees"> = useMemo(() => {
    return {
      employees: employeesCountData?.HrisEmployees?.totalCount ?? undefined,
    };
  }, [employeesCountData]);

  const sync = data?.MergeBackfillRun;
  if (!sync) return null;

  const tabKeys = ["details", "employees"] as const;
  type TabKey = (typeof tabKeys)[number];

  const tabLabels: QueryTabCopy<TabKey> = {
    details: "Details",
    employees: "Modified employees",
  };

  const tabQueryTabRenderFns: QueryTabRenderFns<TabKey> = {
    details: () => <InfoTable items={items} />,
    employees: () => (
      <PaginatedTable<
        HrisEmployeeFragment,
        Column,
        typeof useHrisEmployeesQuery,
        typeof useHrisEmployeesLazyQuery,
        Column
      >
        defaultSort={{ column: "name", descending: true }}
        queryHook={useHrisEmployeesQuery}
        lazyQueryHook={useHrisEmployeesLazyQuery}
        getItems={(x) => x?.HrisEmployees?.objects ?? []}
        getTotalItemCount={(x) => x?.HrisEmployees?.totalCount ?? 0}
        itemId={(x) => x.id}
        input={inputFilters}
        sidebarConfig={{
          tableId: "hris-sync-employees",
          sidebarType: DetailSidebarType.HrisEmployee,
          enabled: true,
        }}
        disableRowClickOnColumns={new Set(["location", "role"])}
        allColumns={[...columns]}
        columnSelection={{
          enabled: true,
          alwaysVisibleColumns: ["name"],
          defaultSelectedColumns: [...columns],
        }}
        columnTitles={{
          name: "Name",
          hrisId: "HRIS ID",
          status: "HRIS Status",
          location: "HRIS Location",
          role: "HRIS Role",
        }}
        renderFns={{
          name: (x) => (
            <div className="flex flex-1 items-center gap-2">
              <UserOutlineIcon className="text-gray-7" />
              <Text
                variant="compact-title"
                ellipsis
                ellipsisParentFlexDirection={"row"}
              >
                {`${x.firstName} ${x.lastName}`}
              </Text>
            </div>
          ),
          status: (x) => x.employmentStatus,
          location: (x) => (
            <div className="flex flex-1 items-center gap-1">
              {x.locationMemberships.length > 0 && (
                <CommaSeparatedLinks
                  items={x.locationMemberships.map((memb) => ({
                    id: memb.location.id,
                    name: memb.location.name,
                  }))}
                  sidebarType={DetailSidebarType.HrisLocation}
                  tableId="hris-locations"
                />
              )}
            </div>
          ),
          role: (x) => (
            <CommaSeparatedLinks
              items={x.teamMemberships.map((memb) => ({
                id: memb.team.id,
                name: memb.team.name,
              }))}
              sidebarType={DetailSidebarType.HrisRole}
              tableId="hris-roles"
            />
          ),
          hrisId: (x) => x.remoteHrisId,
        }}
      />
    ),
  };

  const items: InfoTableItem[] = [
    {
      label: "Integration ID",
      value: sync.hrisIntegration?.id ? (
        <SidebarLink
          id={sync.hrisIntegration.id}
          sidebarType={DetailSidebarType.HrisIntegration}
          tableId="hris-integrations-table"
        >
          {sync.hrisIntegration.id}
        </SidebarLink>
      ) : (
        "--"
      ),
    },
    {
      label: "HRIS Provider",
      value: sync.hrisIntegration?.hrisProvider || "--",
    },
    {
      label: "Nightly resync",
      value: sync.isNightlyResync ? "Yes" : "No",
    },
    {
      label: "Modified employees",
      value: sync.modifiedEmployeesCount?.toString() ?? "--",
    },
    {
      label: "Modified active employees",
      value: sync.modifiedActiveEmployeesCount?.toString() ?? "--",
    },
    {
      label: "Created at",
      value: sync.createdAt
        ? format(parseISO(sync.createdAt), "M/d/yyyy h:mm a")
        : "--",
    },
    {
      label: "Queued at",
      value: sync.queuedAt
        ? format(parseISO(sync.queuedAt), "M/d/yyyy h:mm a")
        : "--",
    },
    {
      label: "Started at",
      value: sync.startedAt
        ? format(parseISO(sync.startedAt), "M/d/yyyy h:mm a")
        : "--",
    },
    {
      label: "Completed at",
      value: sync.completedAt
        ? format(parseISO(sync.completedAt), "M/d/yyyy h:mm a")
        : "--",
    },
  ];

  return (
    <div className="flex h-full flex-col">
      {showHeader && <SidebarHeader />}
      <SidebarTitle
        title={`Sync ${sync.id}`}
        icon={<ArrowsRefreshIcon className="size-[18px] text-gray-9" />}
        sidebarType={DetailSidebarType.HrisSync}
      />
      <SidebarContent>
        <div className="flex min-h-0 flex-1 flex-col">
          <QueryTabs<TabKey>
            tabs={[...tabKeys]}
            labels={tabLabels}
            render={tabQueryTabRenderFns}
            counts={tabCounts as QueryTabCounts<TabKey>}
            defaultTab="details"
            queryParamKey="hris-sync-tab"
          />
        </div>
      </SidebarContent>
    </div>
  );
};

gql`
  query HrisSyncSidebar($id: Int!) {
    MergeBackfillRun(id: $id) {
      id
      createdAt
      updatedAt
      queuedAt
      startedAt
      completedAt
      isNightlyResync
      modifiedEmployeesCount
      modifiedActiveEmployeesCount
      hrisIntegration {
        id
        hrisProvider
      }
    }
  }
`;

export default HrisSyncSidebar;
